import { login, logout, register } from '@/apis/auth.api';
import type { LoginResponse } from '@/types/api/candidate';
import type { IRegisterUser, IUserLogin } from '@/types/common/general';
import { AUTH_CACHE_TTL_MINUTES } from '@/utils/constants';
import { http } from './http.service';

class AuthService {
  async login(user: IUserLogin): Promise<LoginResponse['data']> {
    return login(user).then(response => {
      return response.data as LoginResponse['data'];
    });
  }

  async logout(): Promise<void> {
    try {
      await logout();
    } finally {
      delete http.defaults.headers.common['Authorization'];
      this.clearCache();
    }
  }

  async register(user: IRegisterUser): Promise<unknown> {
    return register(user);
  }

  clearCache(): void {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('auth_state');
    localStorage.removeItem('auth_time');
  }

  getUser(): Record<string, unknown> | null {
    const user = localStorage.getItem('user');

    if (user) {
      try {
        return JSON.parse(user) as Record<string, unknown>;
      } catch {
        return null;
      }
    }

    return null;
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  async isAuthenticated(forceCheck = false): Promise<boolean> {
    const cachedAuth = localStorage.getItem('auth_state');
    const cachedTime = localStorage.getItem('auth_time');

    // trueの場合のみキャッシュを使用
    if (!forceCheck && cachedAuth === 'true' && cachedTime) {
      const timeDiff = (Date.now() - parseInt(cachedTime)) / (1000 * 60);
      if (timeDiff < AUTH_CACHE_TTL_MINUTES) {
        if (!localStorage.getItem('access_token')) {
          localStorage.removeItem('auth_state');
          localStorage.removeItem('auth_time');
          return false;
        }
        return true;
      }
    }

    try {
      const response = await fetch('/api/me', {
        credentials: 'include',
        headers: {
          Authorization: localStorage.getItem('access_token')
            ? `Bearer ${localStorage.getItem('access_token')}`
            : '',
        },
      });

      const isAuthenticated = response.status === 200;

      if (isAuthenticated) {
        // trueの場合のみキャッシュ更新
        localStorage.setItem('auth_state', 'true');
        localStorage.setItem('auth_time', Date.now().toString());
      } else {
        // falseなら削除して非ログイン状態に
        localStorage.removeItem('auth_state');
        localStorage.removeItem('auth_time');
      }
      return isAuthenticated;
    } catch {
      localStorage.removeItem('auth_state');
      localStorage.removeItem('auth_time');
      return false;
    }
  }
}

const instance = new AuthService();

export default instance;
